import { useEffect, useRef, useState } from "react";
import { setRiderPayoutAccount, getBankList, resolveAccountName } from "../api/client";

/**
 * Bank account setup for withdrawals ("money out"). Shared between the
 * rider Dashboard's wallet tab and the standalone Wallet page so there's
 * one source of truth instead of two forms that can drift apart.
 *
 * Props:
 *  - payoutAccount (object|null) — current account, if any
 *  - onUpdated(payoutAccount)    — called after a successful save
 */
export default function PayoutAccountForm({ payoutAccount, onUpdated }) {
  const [form, setForm] = useState({ provider: "paystack", bankCode: "", accountNumber: "", accountName: "", sweepSchedule: "manual" });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // Reflect the rider's already-saved sweep preference once it's loaded,
  // rather than always resetting the toggle back to "manual".
  useEffect(() => {
    if (payoutAccount?.sweepSchedule) {
      setForm((f) => ({ ...f, sweepSchedule: payoutAccount.sweepSchedule }));
    }
  }, [payoutAccount?.sweepSchedule]);

  const [banks, setBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [resolved, setResolved] = useState(false); // true once Paystack/Flutterwave confirmed the name
  const resolveTimer = useRef(null);

  useEffect(() => {
    setBanksLoading(true);
    setBanks([]);
    setForm((f) => ({ ...f, bankCode: "" }));
    getBankList(form.provider)
      .then(({ data }) => setBanks(data))
      .catch(() => setBanks([]))
      .finally(() => setBanksLoading(false));
  }, [form.provider]);

  // Auto-resolve the account holder's name once both a bank and a full
  // account number are picked — debounced so it doesn't fire on every
  // keystroke. Silent on failure: just leaves the name field manually
  // editable, never blocks the form.
  useEffect(() => {
    clearTimeout(resolveTimer.current);
    setResolved(false);
    if (!form.bankCode || form.accountNumber.length !== 10) return;

    resolveTimer.current = setTimeout(async () => {
      setResolving(true);
      try {
        const { data } = await resolveAccountName(form.provider, form.bankCode, form.accountNumber);
        if (data.accountName) {
          setForm((f) => ({ ...f, accountName: data.accountName }));
          setResolved(true);
        }
      } catch {
        // silent — user can still type the account name manually
      } finally {
        setResolving(false);
      }
    }, 500);

    return () => clearTimeout(resolveTimer.current);
  }, [form.bankCode, form.accountNumber, form.provider]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMessage("");
    setError("");
    try {
      const { data } = await setRiderPayoutAccount(form);
      setMessage(
        data.payoutAccount.status === "active"
          ? "✓ Payout account connected — withdrawals will sweep here."
          : "Could not verify this account with the provider — please double-check the details."
      );
      onUpdated?.(data.payoutAccount);
    } catch (err) {
      setError(err.response?.data?.error || "Could not save payout account.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rider-card">
      <h3>Payout Bank Account</h3>
      {payoutAccount?.status === "active" ? (
        <p className="hint" style={{ color: "var(--success)" }}>
          ✓ Connected — {payoutAccount.provider} · {payoutAccount.accountNumber}. Withdrawals sweep here {payoutAccount.sweepSchedule === "daily" ? "daily" : "instantly"}.
        </p>
      ) : (
        <p className="hint">Connect a bank account so withdrawals have somewhere to go.</p>
      )}
      <form onSubmit={handleSubmit} style={{ marginTop: "10px" }}>
        <div className="form-field">
          <label>Provider</label>
          <select value={form.provider} onChange={e => setForm(f => ({ ...f, provider: e.target.value }))}>
            <option value="paystack">Paystack</option>
            <option value="flutterwave">Flutterwave</option>
          </select>
        </div>
        <div className="form-field">
          <label>Bank</label>
          <select
            value={form.bankCode}
            onChange={e => { setForm(f => ({ ...f, bankCode: e.target.value, accountName: "" })); setResolved(false); }}
            disabled={banksLoading}
            required
          >
            <option value="">{banksLoading ? "Loading banks..." : "Select your bank"}</option>
            {banks.map(b => <option key={b.code} value={b.code}>{b.name}</option>)}
          </select>
        </div>
        <div className="form-field">
          <label>Account Number</label>
          <input
            type="text"
            inputMode="numeric"
            maxLength={10}
            value={form.accountNumber}
            onChange={e => { setForm(f => ({ ...f, accountNumber: e.target.value.replace(/\D/g, ""), accountName: "" })); setResolved(false); }}
            required
          />
        </div>
        <div className="form-field">
          <label>Account Name{resolving && " — verifying..."}</label>
          <input
            type="text"
            value={form.accountName}
            onChange={e => { setForm(f => ({ ...f, accountName: e.target.value })); setResolved(false); }}
            placeholder={resolving ? "Looking up account name..." : "Auto-fills once bank + account number are entered"}
            required
          />
          {resolved && <p className="hint" style={{ color: "var(--success)", marginTop: "4px" }}>✓ Verified with {form.provider === "flutterwave" ? "Flutterwave" : "Paystack"}</p>}
        </div>
        <div className="form-field">
          <label>Auto-sweep</label>
          <select value={form.sweepSchedule} onChange={e => setForm(f => ({ ...f, sweepSchedule: e.target.value }))}>
            <option value="manual">Manual — I'll withdraw myself (default)</option>
            <option value="instant">Instant — auto-transfer after every delivery</option>
            <option value="daily">Daily — auto-transfer once a day</option>
          </select>
          <p className="hint" style={{ marginTop: "4px" }}>
            {form.sweepSchedule === "manual"
              ? "Funds stay in your wallet until you tap Withdraw."
              : form.sweepSchedule === "instant"
                ? "Your balance sweeps to this bank account automatically right after every completed delivery."
                : "Your balance sweeps to this bank account automatically once every 24 hours."}
          </p>
        </div>
        {error && <p className="error">{error}</p>}
        {message && <p className="hint" style={{ color: "var(--success)" }}>{message}</p>}
        <button type="submit" disabled={busy}>
          {busy ? "Saving..." : payoutAccount?.status === "active" ? "Update Payout Account" : "Connect Payout Account"}
        </button>
      </form>
    </div>
  );
}
